import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import backendApi from '../shared/api/backendApi';

function Profit() {
  const [profitData, setProfitData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfitData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await backendApi.getProfitSummary();
      // Database API returns { success: true, profit: [...], generatedAt: string }
      const data = response.profit || [];
      setProfitData(data);
    } catch (err) {
      setError('Failed to load profit data. Please try again.');
      console.error('Error loading profit data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfitData();
  }, [loadProfitData]);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }),
    []
  );

  const totalProfit = useMemo(
    () => profitData.reduce((sum, entry) => sum + parseFloat(entry['Total Profit'] || 0), 0),
    [profitData]
  );

  const profitRows = useMemo(
    () => profitData.slice().reverse().map((entry, index) => {
      const items = (entry['Items'] || '').split(', ');
      const quantities = (entry['Quantities'] || '').split(',').map((q) => q.trim());
      const formattedItems = items
        .map((item, itemIndex) => {
          const qty = quantities[itemIndex] || '1';
          return `${item.trim()} x ${qty}`;
        })
        .join(', ');

      const purchasePrice = parseFloat(entry['Purchase Prices'] || 0);
      const totalPurchase = parseFloat(entry['Total Purchase'] || 0);
      const totalRevenue = parseFloat(entry['Total Revenue'] || entry['Selling Price'] || 0);
      const profitPerUnit = parseFloat(entry['Profit/Unit'] || 0);
      const totalProfitValue = parseFloat(entry['Total Profit'] || 0);

      return {
        key: `${entry['Date'] || 'date'}-${entry['Items'] || 'items'}-${index}`,
        index,
        date: entry['Date'],
        formattedItems,
        purchasePriceFormatted: currencyFormatter.format(purchasePrice),
        totalPurchaseFormatted: currencyFormatter.format(totalPurchase),
        totalRevenueFormatted: currencyFormatter.format(totalRevenue),
        profitPerUnitFormatted: currencyFormatter.format(profitPerUnit),
        totalProfitFormatted: currencyFormatter.format(totalProfitValue),
        profitPerUnitColor: profitPerUnit >= 0 ? 'success.main' : 'error.main',
        totalProfitColor: totalProfitValue >= 0 ? 'success.main' : 'error.main',
      };
    }),
    [currencyFormatter, profitData]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header section with title and total profit card side by side */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        {/* Left side - Title and description */}
        <Box sx={{ flex: 1, mr: 3 }}>
          <Typography variant="h4" gutterBottom>
            Profit Analysis
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Profit calculations are automatically generated from sales and purchase data.
          </Typography>
        </Box>

        {/* Right side - Total Profit Card */}
        <Box sx={{ flexShrink: 0, width: 280 }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Total Profit
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {'\u20B9'}{currencyFormatter.format(totalProfit)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                From all profit entries
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, maxHeight: '75vh' }} stickyHeader>
        <Table>
          <TableHead sx={{ backgroundColor: '#1e293b', position: 'sticky', top: 0, zIndex: 1 }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Items & Qty</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Purchase Price</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Purchase</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Revenue (Final Amount)</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Profit/Unit</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Profit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profitRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No profit data yet. Add sales and purchases to see profit analysis.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              profitRows.map((row) => (
                <TableRow
                  key={row.key}
                  sx={{
                    backgroundColor: row.index % 2 === 0 ? 'white' : '#f8fafc',
                    '&:hover': { backgroundColor: '#e2e8f0' },
                    minHeight: 60,
                    '& .MuiTableCell-root': {
                      padding: '12px 8px',
                    }
                  }}
                >
                  <TableCell>{row.date}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {row.formattedItems}
                    </Typography>
                  </TableCell>
                  <TableCell>{'\u20B9'}{row.purchasePriceFormatted}</TableCell>
                  <TableCell>{'\u20B9'}{row.totalPurchaseFormatted}</TableCell>
                  <TableCell>{'\u20B9'}{row.totalRevenueFormatted}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: row.profitPerUnitColor }}>
                      {'\u20B9'}{row.profitPerUnitFormatted}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, color: row.totalProfitColor }}>
                      {'\u20B9'}{row.totalProfitFormatted}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Profit;
